import { useId, useRef, useMemo, useCallback, useEffect } from 'react';
import { useDatePickerState } from '@react-stately/datepicker';
import {
  useDatePicker,
  useDateField,
  useDateSegment,
  useTimeField,
} from '@react-aria/datepicker';
import { useDateFieldState, useTimeFieldState } from '@react-stately/datepicker';
import { useLocale } from '@react-aria/i18n';
import { useTranslation } from '@hooks/useTranslation';
import { useDialog } from '@react-aria/dialog';
import { FocusScope } from '@react-aria/focus';
import { useButton } from '@react-aria/button';
import { parseDateTime, parseDate, createCalendar, today, getLocalTimeZone } from '@internationalized/date';
import type {
  CalendarDate,
  CalendarDateTime,
  DateValue,
} from '@internationalized/date';
import type { TimeValue } from '@react-aria/datepicker';
import type { DateFieldState, DateSegment } from '@react-stately/datepicker';
import type { InputSize } from '@components/ui/Input/TextInput';
import FormField from '@components/ui/Input/FormField';
import type { DatePickerPlacement } from '@components/ui/DatePicker/DatePicker';
import Calendar from '@components/ui/DatePicker/Calendar';
import datePickerStyles from '@components/ui/DatePicker/DatePicker.module.scss';
import styles from './DateTimePicker.module.scss';
import inputStyles from '@components/ui/Input/Input.module.scss';

// ============================================
// TYPES
// ============================================

/** Props for the DateTimePicker component. */
export interface DateTimePickerProps {
  /** Selected datetime as ISO string: 'YYYY-MM-DDTHH:mm' or 'YYYY-MM-DDTHH:mm:ss' */
  value?: string;
  /** Called when the datetime changes, receives ISO datetime string */
  onChange?: (value: string) => void;
  /** Label text displayed above the input */
  label?: string;
  /** Show required asterisk on the label */
  isRequired?: boolean;
  /** Help text below the input (hidden when error is present) */
  helperText?: string;
  /** Error message — replaces helperText, applies error border */
  error?: string;
  /** Placeholder text (segments show placeholders natively) */
  placeholder?: string;
  /** Input size variant */
  size?: InputSize;
  /** Stretch to fill parent width */
  fullWidth?: boolean;
  /** Whether the datetime picker is disabled */
  disabled?: boolean;
  /** Minimum selectable date as ISO string: 'YYYY-MM-DD' */
  minDate?: string;
  /** Maximum selectable date as ISO string: 'YYYY-MM-DD' */
  maxDate?: string;
  /** 12-hour or 24-hour clock */
  hourCycle?: 12 | 24;
  /** Smallest time unit to display */
  granularity?: 'hour' | 'minute' | 'second';
  /** Function to determine if a date is unavailable, receives ISO date string */
  isDateUnavailable?: (dateIso: string) => boolean;
  /** Popover placement relative to the input */
  placement?: DatePickerPlacement;
  /** Additional className on the outer wrapper */
  className?: string;
  /** Override auto-generated ID */
  id?: string;
  /** Forwarded ref to the field group element */
  ref?: React.Ref<HTMLDivElement>;
}

// ============================================
// HELPERS
// ============================================

function toCalendarDateTime(
  iso: string | undefined,
): CalendarDateTime | undefined {
  if (!iso) return undefined;
  try {
    return parseDateTime(iso);
  } catch {
    return undefined;
  }
}

function toCalendarDate(iso: string | undefined): CalendarDate | undefined {
  if (!iso) return undefined;
  try {
    return parseDate(iso);
  } catch {
    return undefined;
  }
}

// ============================================
// CALENDAR ICON
// ============================================

const CalendarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M5 1v2M11 1v2M1.5 6h13M2.5 3h11a1 1 0 011 1v10a1 1 0 01-1 1h-11a1 1 0 01-1-1V4a1 1 0 011-1z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// ============================================
// DATE TIME PICKER COMPONENT
// ============================================

/**
 * A combined date and time picker with segment-based input and calendar popover.
 * The calendar popover includes a time field below the calendar grid.
 * Accepts and emits ISO datetime strings for easy API/form integration.
 * Built on react-aria for full accessibility and keyboard navigation.
 */
function DateTimePicker({
  ref,
  value,
  onChange,
  label,
  isRequired = false,
  helperText,
  error,
  placeholder: _placeholder,
  size = 'md',
  fullWidth = true,
  disabled = false,
  minDate,
  maxDate,
  hourCycle,
  granularity = 'minute',
  isDateUnavailable,
  placement = 'bottom-start',
  className,
  id: idProp,
}: DateTimePickerProps) {
  const { t } = useTranslation();
  const autoId = useId();
  const id = idProp ?? autoId;
  const hasError = Boolean(error);

  const parsedValue = useMemo(
    () => toCalendarDateTime(value) ?? null,
    [value],
  );
  const parsedMinDate = useMemo(() => toCalendarDate(minDate), [minDate]);
  const parsedMaxDate = useMemo(() => toCalendarDate(maxDate), [maxDate]);

  const ariaIsDateUnavailable = useMemo(() => {
    if (!isDateUnavailable) return undefined;
    return (date: DateValue) => {
      const iso = date.toString();
      return isDateUnavailable(iso);
    };
  }, [isDateUnavailable]);

  const handleChange = useCallback(
    (dt: DateValue | null) => {
      if (dt && onChange) {
        onChange(dt.toString());
      }
    },
    [onChange],
  );

  const state = useDatePickerState({
    value: parsedValue,
    onChange: handleChange,
    minValue: parsedMinDate,
    maxValue: parsedMaxDate,
    isDateUnavailable: ariaIsDateUnavailable,
    isDisabled: disabled,
    granularity,
    hourCycle,
    shouldCloseOnSelect: false, // Keep open for time selection
  });

  const groupRef = useRef<HTMLDivElement>(null);
  const { groupProps, fieldProps, buttonProps, dialogProps } = useDatePicker(
    {
      label: label ?? 'Date and time',
      value: parsedValue,
      onChange: handleChange,
      minValue: parsedMinDate,
      maxValue: parsedMaxDate,
      isDateUnavailable: ariaIsDateUnavailable,
      isDisabled: disabled,
      isRequired,
      granularity,
      hourCycle,
      'aria-label': label ?? 'Date and time',
    },
    state,
    groupRef,
  );

  const describedBy = hasError
    ? `${id}-error`
    : helperText
      ? `${id}-helper`
      : undefined;

  const sizeClass = inputStyles[size] ?? inputStyles.md;

  const fieldClassName = [sizeClass, fullWidth && inputStyles.fullWidth, className]
    .filter(Boolean)
    .join(' ');

  const inputWrapperClasses = [
    datePickerStyles.dateInput,
    inputStyles.inputBase,
    hasError && datePickerStyles.dateInputError,
    disabled && datePickerStyles.dateInputDisabled,
  ]
    .filter(Boolean)
    .join(' ');

  // Extract the CalendarDate from the dateValue for the Calendar component
  const calendarDateValue = useMemo((): CalendarDate | null => {
    const dv = state.dateValue;
    if (!dv) return null;
    // dateValue from the state is a DateValue, cast to CalendarDate for the calendar
    return new (parseDate('2000-01-01').constructor as typeof CalendarDate)(
      dv.year,
      dv.month,
      dv.day,
    );
  }, [state.dateValue]);

  return (
    <FormField
      id={id}
      label={label}
      isRequired={isRequired}
      helperText={helperText}
      error={error}
      isDisabled={disabled}
      className={fieldClassName}
    >
      <div className={datePickerStyles.inputWrapper}>
        <div {...groupProps} ref={groupRef} className={datePickerStyles.inputGroup}>
          <DateTimeField
            {...fieldProps}
            ref={ref}
            inputClassName={inputWrapperClasses}
            describedBy={describedBy}
          />
          <DateTimeCalendarButton {...buttonProps} isDisabled={disabled} />
        </div>

        {state.isOpen && (
          <DateTimePopover
            dialogProps={dialogProps}
            onClose={() => state.setOpen(false)}
            placement={placement}
          >
            <Calendar
              value={calendarDateValue}
              onChange={(date) => {
                state.setDateValue(date);
              }}
              minValue={parsedMinDate}
              maxValue={parsedMaxDate}
              isDateUnavailable={
                isDateUnavailable
                  ? (date) => isDateUnavailable(date.toString())
                  : undefined
              }
            />
            <div className={datePickerStyles.calendarFooter}>
              <button
                type="button"
                className={datePickerStyles.todayButton}
                onClick={() => {
                  const todayDate = today(getLocalTimeZone());
                  state.setDateValue(todayDate);
                }}
              >
                {t('K_BUTTON_TODAY', 'Today')}
              </button>
            </div>
            <div className={styles.timeSection}>
              <span className={styles.timeLabel}>{t('K_LABEL_TIME', 'Time')}</span>
              <PopoverTimeField
                value={state.timeValue}
                onChange={(time) => { if (time) state.setTimeValue(time); }}
                hourCycle={hourCycle}
                granularity={granularity}
              />
            </div>
          </DateTimePopover>
        )}
      </div>
    </FormField>
  );
}

// ============================================
// DATE TIME FIELD — segment-based input
// ============================================

interface DateTimeFieldInternalProps {
  ref?: React.Ref<HTMLDivElement>;
  inputClassName?: string;
  describedBy?: string;
}

function DateTimeField({
  ref,
  inputClassName,
  describedBy,
  ...props
}: DateTimeFieldInternalProps & Record<string, unknown>) {
  const { locale } = useLocale();
  const fieldRef = useRef<HTMLDivElement>(null);

  const state = useDateFieldState({
    ...props,
    locale,
    createCalendar,
  });

  const { fieldProps } = useDateField(
    { ...props, 'aria-describedby': describedBy },
    state,
    fieldRef,
  );

  const setRefs = useCallback(
    (node: HTMLDivElement | null) => {
      (fieldRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref && typeof ref === 'object') {
        (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
      }
    },
    [ref],
  );

  return (
    <div className={inputClassName}>
      <div {...fieldProps} ref={setRefs} className={datePickerStyles.segmentsWrapper}>
        {state.segments.map((segment, i) => (
          <SegmentView key={i} segment={segment} state={state} />
        ))}
      </div>
    </div>
  );
}

// ============================================
// SEGMENT VIEW — individual editable segment
// ============================================

interface SegmentViewProps {
  segment: DateSegment;
  state: DateFieldState;
}

function SegmentView({ segment, state }: SegmentViewProps) {
  const ref = useRef<HTMLDivElement>(null);
  const { segmentProps } = useDateSegment(segment, state, ref);

  const isLiteral = segment.type === 'literal';

  return (
    <span
      {...segmentProps}
      ref={ref}
      className={
        isLiteral ? datePickerStyles.separator : datePickerStyles.segment
      }
      data-placeholder={segment.isPlaceholder || undefined}
    >
      {segment.text}
    </span>
  );
}

// ============================================
// CALENDAR BUTTON
// ============================================

interface DateTimeCalendarButtonInternalProps {
  isDisabled?: boolean;
}

function DateTimeCalendarButton({
  isDisabled,
  ...props
}: DateTimeCalendarButtonInternalProps & Record<string, unknown>) {
  const ref = useRef<HTMLButtonElement>(null);
  const { buttonProps } = useButton(
    { ...props, isDisabled } as Parameters<typeof useButton>[0],
    ref,
  );

  return (
    <button
      {...buttonProps}
      ref={ref}
      className={datePickerStyles.calendarButton}
      disabled={isDisabled}
      tabIndex={-1}
    >
      <CalendarIcon />
    </button>
  );
}

// ============================================
// POPOVER — calendar + time
// ============================================

interface DateTimePopoverProps {
  dialogProps: ReturnType<typeof useDatePicker>['dialogProps'];
  children: React.ReactNode;
  onClose: () => void;
  placement?: DatePickerPlacement;
}

function DateTimePopover({ dialogProps, children, onClose, placement = 'bottom-start' }: DateTimePopoverProps) {
  const popoverRef = useRef<HTMLDivElement>(null);
  const { dialogProps: ariaDialogProps } = useDialog(dialogProps, popoverRef);

  // Close on outside click
  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [onClose]);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const placementClass = datePickerStyles[`popover--${placement}`] ?? '';
  const popoverClasses = [datePickerStyles.popover, placementClass].filter(Boolean).join(' ');

  return (
    <FocusScope restoreFocus>
      <div {...ariaDialogProps} ref={popoverRef} className={popoverClasses}>
        {children}
      </div>
    </FocusScope>
  );
}

// ============================================
// POPOVER TIME FIELD — time segments inside popover
// ============================================

interface PopoverTimeFieldProps {
  value: TimeValue | null;
  onChange: (value: TimeValue | null) => void;
  hourCycle?: 12 | 24;
  granularity?: 'hour' | 'minute' | 'second';
}

function PopoverTimeField({
  value,
  onChange,
  hourCycle,
  granularity = 'minute',
}: PopoverTimeFieldProps) {
  const { locale } = useLocale();
  const fieldRef = useRef<HTMLDivElement>(null);

  const state = useTimeFieldState({
    value,
    onChange,
    hourCycle,
    granularity,
    locale,
  });

  const { fieldProps } = useTimeField(
    {
      label: 'Time',
      value,
      onChange,
      hourCycle,
      granularity,
      'aria-label': 'Time',
    },
    state,
    fieldRef,
  );

  return (
    <div {...fieldProps} ref={fieldRef} className={styles.popoverTimeInput}>
      {state.segments.map((segment, i) => (
        <SegmentView key={i} segment={segment} state={state} />
      ))}
    </div>
  );
}

export default DateTimePicker;
